/**
 * CoachMarkTour — гид-тур по главному экрану после первичного онбординга.
 *
 * Концепция (админ 17 Apr 2026):
 *   - После 5 шагов первичного онбординга + закрытия overlay,
 *     юзер попадает на главный экран. Через 300мс стартует тур.
 *   - Тёмная заливка ВСЕГО экрана с "окном" spotlight над одним
 *     элементом (по data-tour атрибуту).
 *   - Рядом с элементом — каллаут-пузырёк с текстом пояснения.
 *   - Юзер НЕ может тапнуть нигде кроме "Далее" в пузыре.
 *     (Реального перехода на таб не происходит — тур сам ведёт.)
 *   - В конце — финальный пузырёк "Готово" → закрывается,
 *     localStorage['luna_tour_seen']='1' чтобы не показывать снова.
 *
 * Визуал:
 *   - Backdrop: rgba(0,0,0,0.72) + SVG <mask> с вырезанным
 *     скруглённым прямоугольником над target (spotlight).
 *   - Пузырёк: glass-подложка, premium тон, spring-entry.
 *   - Анимации: SPRING_SCREEN, stagger 60мс.
 */
import { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useTranslation, Trans } from 'react-i18next';
import { useTelegramWebApp } from '@/hooks/useTelegramWebApp';
import { lunaApi } from '@/services/api';
import { SPRING_CARD, EASE_SCREEN } from './springs';
import { useSounds } from './useSounds';

interface TourStepDef {
  target: string;
  titleKey: string;
  bodyKey: string;
  placement?: 'top' | 'bottom' | 'auto';
}

const STEPS: TourStepDef[] = [
  { target: 'balance',     titleKey: 'onboarding.tour.step1Title', bodyKey: 'onboarding.tour.step1Body', placement: 'bottom' },
  { target: 'bell',        titleKey: 'onboarding.tour.step2Title', bodyKey: 'onboarding.tour.step2Body', placement: 'bottom' },
  { target: 'nav-slots',   titleKey: 'onboarding.tour.step3Title', bodyKey: 'onboarding.tour.step3Body', placement: 'top' },
  { target: 'nav-network', titleKey: 'onboarding.tour.step4Title', bodyKey: 'onboarding.tour.step4Body', placement: 'top' },
  { target: 'nav-quick',   titleKey: 'onboarding.tour.step5Title', bodyKey: 'onboarding.tour.step5Body', placement: 'top' },
  { target: 'nav-wallet',  titleKey: 'onboarding.tour.step6Title', bodyKey: 'onboarding.tour.step6Body', placement: 'top' },
];

const TOUR_SEEN_KEY = 'luna_tour_seen';
const SPOTLIGHT_PADDING = 8; // отступ вокруг подсвеченного элемента (px)
const BUBBLE_GAP = 18; // расстояние между bubble и элементом

interface Rect {
  left: number;
  top: number;
  width: number;
  height: number;
}

function getRect(selector: string): Rect | null {
  const el = document.querySelector<HTMLElement>(`[data-tour="${selector}"]`);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  // Если элемент есть но 0×0 (ещё скрыт/анимируется) — считаем не готов
  if (r.width < 4 || r.height < 4) return null;
  return { left: r.left, top: r.top, width: r.width, height: r.height };
}

/**
 * Показывается автоматически когда:
 *   - onboarding.completedAt уже установлен (первичный онбординг пройден)
 *   - localStorage['luna_tour_seen'] !== '1'
 * Компонент сам poll'ит status API каждые 2 сек пока OnboardingShell
 * активен (не хотим мешать первичному онбордингу).
 */
export function CoachMarkTour() {
  const onFinish = () => {
    /* noop — компонент скрывает себя сам */
  };
  const { t } = useTranslation();
  const { haptic } = useTelegramWebApp();
  const sounds = useSounds();
  const [stepIdx, setStepIdx] = useState(-1); // -1 = не показан
  const [showFinal, setShowFinal] = useState(false);
  const [rect, setRect] = useState<Rect | null>(null);
  const [viewport, setViewport] = useState({
    w: typeof window !== 'undefined' ? window.innerWidth : 0,
    h: typeof window !== 'undefined' ? window.innerHeight : 0,
  });

  // Старт тура — 3 независимых триггера для надёжности:
  //   1. Event 'luna:onboarding-done' от OnboardingShell (мгновенно при завершении)
  //   2. Periodic poll /status каждые 1 сек пока tour_seen!=1 и completedAt!=null.
  //      Работает и когда юзер UJE прошёл онбоардинг в прошлую сессию.
  //   3. Ручной старт через event 'luna:tour-force-start' (для отладки).
  //
  // started — в ref чтобы React не пересоздавал через re-render.
  // Cleanup через cancelled flag + clearInterval.
  useEffect(() => {
    if (typeof window === 'undefined') return;

    let cancelled = false;
    const ref = { started: false };
    const log = (msg: string, extra?: unknown) => {
      // eslint-disable-next-line no-console
      console.log(`[CoachMarkTour] ${msg}`, extra ?? '');
    };

    const startTour = (delay = 300) => {
      if (ref.started) return;
      if (window.localStorage.getItem(TOUR_SEEN_KEY) === '1') {
        log('aborted — tour_seen=1');
        return;
      }
      // Гейт оферты: пока юзер не принял условия, тур запускать нельзя —
      // OfertaShell рендерится поверх (z=9100) и target-элементы за ним
      // не видны. После accept OfertaShell диспатчит 'luna:oferta-accepted'
      // и повторно вызовет startTour — уже этот guard пропустит.
      void (async () => {
        try {
          const r = await lunaApi.oferta.status();
          if (r.data?.success && r.data.shouldShow) {
            log('aborted — oferta not yet accepted');
            return;
          }
        } catch {
          // Если /status недоступен — не блокируем тур (лучше показать чем нет)
        }
        if (ref.started) return;
        ref.started = true;
        log('startTour', { delay });
        window.setTimeout(() => {
          if (cancelled) return;
          log('setStepIdx(0) NOW');
          setStepIdx(0);
        }, delay);
      })();
    };

    const checkStatus = async () => {
      if (cancelled || ref.started) return;
      if (window.localStorage.getItem(TOUR_SEEN_KEY) === '1') return;
      try {
        const res = await lunaApi.onboarding.status();
        const data = res.data as {
          success?: boolean;
          status?: { completedAt?: string | null };
        };
        const completedAt = data?.status?.completedAt ?? null;
        log('poll', { completedAt });
        if (!cancelled && completedAt) {
          startTour(700);
        }
      } catch (e) {
        log('poll error', e);
      }
    };

    const onDone = () => {
      log('event luna:onboarding-done');
      // Ждём пока OnboardingShell exit-анимация завершится (~700ms)
      // чтобы overlay перестал перекрывать target элементы
      startTour(1200);
    };
    const onOfertaAccepted = () => {
      log('event luna:oferta-accepted');
      // Оферта скрыта — можно разбудить guard и стартовать тур.
      ref.started = false;
      startTour(900);
    };
    const onForce = () => {
      log('event luna:tour-force-start');
      ref.started = false;
      startTour(100);
    };
    window.addEventListener('luna:onboarding-done', onDone);
    window.addEventListener('luna:oferta-accepted', onOfertaAccepted);
    window.addEventListener('luna:tour-force-start', onForce);

    // Первый check быстро, далее polling каждую секунду
    void checkStatus();
    const poll = window.setInterval(() => {
      if (cancelled || ref.started) {
        window.clearInterval(poll);
        return;
      }
      void checkStatus();
    }, 1000);

    // Останавливаем polling через 2 минуты на всякий случай
    const hardStop = window.setTimeout(() => {
      window.clearInterval(poll);
    }, 120_000);

    return () => {
      cancelled = true;
      window.removeEventListener('luna:onboarding-done', onDone);
      window.removeEventListener('luna:oferta-accepted', onOfertaAccepted);
      window.removeEventListener('luna:tour-force-start', onForce);
      window.clearInterval(poll);
      window.clearTimeout(hardStop);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // При смене шага — находим target с настойчивыми retries
  useEffect(() => {
    if (stepIdx < 0 || stepIdx >= STEPS.length) return;
    const step = STEPS[stepIdx];
    if (!step) return;

    setRect(null); // сбрасываем пока ищем новый target
    let attempts = 0;
    let cancelled = false;
    let timer: number | null = null;

    const find = () => {
      if (cancelled) return;
      const r = getRect(step.target);
      if (r) {
        setRect(r);
        return;
      }
      // Ретрай до 30 раз с шагом 150мс = 4.5 сек
      if (attempts < 30) {
        attempts++;
        timer = window.setTimeout(find, 150);
      } else {
        // Пропускаем шаг но ждём 600мс перед следующим
        timer = window.setTimeout(() => {
          if (!cancelled) setStepIdx((i) => i + 1);
        }, 600);
      }
    };
    find();

    return () => {
      cancelled = true;
      if (timer !== null) window.clearTimeout(timer);
    };
  }, [stepIdx]);

  // Viewport resize tracker (resize/orientationchange)
  useEffect(() => {
    const onR = () =>
      setViewport({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener('resize', onR);
    window.addEventListener('orientationchange', onR);
    return () => {
      window.removeEventListener('resize', onR);
      window.removeEventListener('orientationchange', onR);
    };
  }, []);

  const handleNext = useCallback(() => {
    haptic.medium();
    if (stepIdx + 1 >= STEPS.length) {
      // Последний шаг — показываем финальное поздравление
      sounds.milestone();
      setShowFinal(true);
    } else {
      sounds.coin();
      sounds.swipe();
      setStepIdx((i) => i + 1);
    }
  }, [stepIdx, haptic, sounds]);

  const handleFinalClose = useCallback(() => {
    haptic.success();
    sounds.success();
    window.localStorage.setItem(TOUR_SEEN_KEY, '1');
    setShowFinal(false);
    setStepIdx(-1);
    onFinish();
    // Только ТЕПЕРЬ — в самом конце (после тура и финального окна) —
    // показываем тост "+$1 welcome bonus" справа вверху.
    window.setTimeout(() => {
      window.dispatchEvent(
        new CustomEvent('luna:welcome-bonus', {
          detail: { amount: '1.00' },
        }),
      );
    }, 400);
  }, [haptic, sounds]);

  const handleSkip = useCallback(() => {
    haptic.warning();
    sounds.tap();
    window.localStorage.setItem(TOUR_SEEN_KEY, '1');
    setStepIdx(-1);
    onFinish();
  }, [haptic, sounds]);

  // Финальное поздравление после последнего tour-шага
  if (showFinal) {
    return <TourFinale onClose={handleFinalClose} />;
  }

  if (stepIdx < 0 || !rect || stepIdx >= STEPS.length) return null;
  const step = STEPS[stepIdx];
  if (!step) return null;

  // Spotlight прямоугольник с padding
  const sp = {
    left: Math.max(0, rect.left - SPOTLIGHT_PADDING),
    top: Math.max(0, rect.top - SPOTLIGHT_PADDING),
    width: rect.width + SPOTLIGHT_PADDING * 2,
    height: rect.height + SPOTLIGHT_PADDING * 2,
  };

  // Определяем placement пузыря
  const targetCenterY = rect.top + rect.height / 2;
  const placementAuto = targetCenterY < viewport.h / 2 ? 'bottom' : 'top';
  const placement = step.placement === 'auto' ? placementAuto : step.placement ?? placementAuto;

  // Ширина bubble и left-координата (clamp к краям viewport)
  const VIEWPORT_PAD = 12;
  const bubbleW = Math.min(viewport.w - VIEWPORT_PAD * 2, 320);
  const targetCenterX = rect.left + rect.width / 2;
  const bubbleLeft = Math.max(
    VIEWPORT_PAD,
    Math.min(
      viewport.w - bubbleW - VIEWPORT_PAD,
      targetCenterX - bubbleW / 2,
    ),
  );

  // Для placement=top — якорим НИЖНИЙ край bubble к верху target
  // (через CSS bottom), чтобы высота bubble не выходила за экран.
  // Для placement=bottom — якорим верхний край к низу target.
  const bubbleAnchor: Record<string, number | string | undefined> =
    placement === 'top'
      ? {
          bottom: viewport.h - rect.top + BUBBLE_GAP,
          top: undefined,
        }
      : {
          top: rect.top + rect.height + BUBBLE_GAP,
          bottom: undefined,
        };

  const isLast = stepIdx === STEPS.length - 1;
  const progress = `${stepIdx + 1} / ${STEPS.length}`;

  return (
    <AnimatePresence>
      <motion.div
        key={`tour-${stepIdx}`}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={EASE_SCREEN}
        className="fixed inset-0 z-[8800]"
        style={{ touchAction: 'none' }}
      >
        {/* SVG mask: тёмная заливка с вырезанным окном */}
        <svg
          width={viewport.w}
          height={viewport.h}
          className="fixed inset-0 pointer-events-none"
          style={{ width: '100vw', height: '100vh' }}
        >
          <defs>
            <mask id="tour-mask">
              <rect width="100%" height="100%" fill="white" />
              <rect
                x={sp.left}
                y={sp.top}
                width={sp.width}
                height={sp.height}
                rx={16}
                ry={16}
                fill="black"
              />
            </mask>
          </defs>
          <rect
            width="100%"
            height="100%"
            fill="rgba(4,6,14,0.78)"
            mask="url(#tour-mask)"
          />
        </svg>

        {/* Пульсирующая рамка вокруг spotlight */}
        <motion.div
          aria-hidden
          className="fixed pointer-events-none"
          style={{
            left: sp.left,
            top: sp.top,
            width: sp.width,
            height: sp.height,
            borderRadius: 16,
            border: '2px solid rgba(245,230,168,0.85)',
            boxShadow:
              '0 0 0 2px rgba(234,196,96,0.3), 0 0 40px 4px rgba(234,196,96,0.45)',
          }}
          animate={{
            scale: [1, 1.03, 1],
            opacity: [0.85, 1, 0.85],
          }}
          transition={{
            duration: 2,
            ease: 'easeInOut',
            repeat: Infinity,
          }}
        />

        {/* Клик-перехватчик на весь экран — блокирует тапы (кроме пузыря) */}
        <div className="fixed inset-0 pointer-events-auto" onClick={() => {}} />

        {/* Callout-пузырь */}
        <BubbleCallout
          anchor={bubbleAnchor}
          left={bubbleLeft}
          width={bubbleW}
          placement={placement}
          progress={progress}
          title={t(step.titleKey)}
          body={t(step.bodyKey)}
          isLast={isLast}
          onNext={handleNext}
          onSkip={handleSkip}
        />
      </motion.div>
    </AnimatePresence>
  );
}

// ---------------------------------------------------------------------------
// TourFinale — большое центральное окно после всех tour-шагов
// ---------------------------------------------------------------------------
function TourFinale({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={EASE_SCREEN}
      className="fixed inset-0 z-[8900] flex items-center justify-center px-6"
      style={{
        background:
          'radial-gradient(120% 80% at 50% 40%, rgba(20,24,46,0.92) 0%, rgba(6,8,18,0.94) 60%, rgba(4,5,12,0.96) 100%)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={SPRING_CARD}
        className="w-full max-w-[360px] text-center"
        style={{
          padding: 22,
          borderRadius: 26,
          background: 'rgba(16,20,36,0.92)',
          backdropFilter: 'blur(28px) saturate(180%)',
          WebkitBackdropFilter: 'blur(28px) saturate(180%)',
          border: '1px solid rgba(255,255,255,0.14)',
          boxShadow:
            '0 32px 64px -20px rgba(0,0,0,0.85), inset 0 1px 0 rgba(255,255,255,0.16)',
        }}
      >
        <p
          className="font-mono"
          style={{
            fontSize: 10,
            letterSpacing: '0.3em',
            textTransform: 'uppercase',
            color: 'rgba(220,230,255,0.7)',
          }}
        >
          {t('onboarding.tour.welcomeKicker')}
        </p>
        <h3
          className="mt-2"
          style={{
            fontSize: 22,
            fontWeight: 800,
            color: '#f5e6a8',
            letterSpacing: '-0.02em',
            lineHeight: 1.2,
            textShadow: '0 0 30px rgba(234,196,96,0.35)',
            fontFamily:
              '-apple-system, BlinkMacSystemFont, "SF Pro Display", system-ui, sans-serif',
          }}
        >
          {t('onboarding.tour.welcomeTitle')}
        </h3>
        <p
          className="mt-3"
          style={{
            fontSize: 13.5,
            lineHeight: 1.5,
            color: 'rgba(225,232,245,0.9)',
          }}
        >
          <Trans i18nKey="onboarding.tour.welcomeBodyHTML" components={{ b: <strong /> }} />
        </p>
        <div
          className="mt-4 px-3 py-2.5 rounded-[14px] text-left"
          style={{
            background: 'rgba(140,100,220,0.12)',
            border: '1px solid rgba(170,130,240,0.22)',
          }}
        >
          <p
            className="font-mono"
            style={{
              fontSize: 9,
              letterSpacing: '0.22em',
              textTransform: 'uppercase',
              color: 'rgba(200,180,255,0.75)',
            }}
          >
            {t('onboarding.tour.jarvisHint')}
          </p>
          <p
            className="mt-1"
            style={{
              fontSize: 12,
              lineHeight: 1.4,
              color: 'rgba(220,220,245,0.85)',
            }}
          >
            <Trans i18nKey="onboarding.tour.jarvisHintBodyHTML" components={{ b: <strong /> }} />
          </p>
        </div>
        <motion.button
          type="button"
          onClick={onClose}
          whileTap={{ scale: 0.97 }}
          transition={SPRING_CARD}
          className="mt-5 w-full px-6 py-3.5 rounded-full font-semibold"
          style={{
            fontSize: 14,
            color: '#06091c',
            background: 'linear-gradient(180deg, #f5e6a8 0%, #e9c76a 100%)',
            border: '1px solid rgba(255,230,150,0.5)',
            boxShadow:
              '0 12px 28px -8px rgba(234,196,96,0.55), inset 0 1px 0 rgba(255,255,255,0.5)',
            cursor: 'pointer',
            WebkitTapHighlightColor: 'transparent',
          }}
        >
          {t('onboarding.tour.startBtn')}
        </motion.button>
      </motion.div>
    </motion.div>
  );
}

// ---------------------------------------------------------------------------
// BubbleCallout
// ---------------------------------------------------------------------------
interface BubbleCalloutProps {
  anchor: Record<string, number | string | undefined>;
  left: number;
  width: number;
  placement: 'top' | 'bottom';
  progress: string;
  title: string;
  body: string;
  isLast: boolean;
  onNext: () => void;
  onSkip: () => void;
}

function BubbleCallout({
  anchor,
  left,
  width,
  placement,
  progress,
  title,
  body,
  isLast,
  onNext,
  onSkip,
}: BubbleCalloutProps) {
  const { t } = useTranslation();
  return (
    <motion.div
      initial={{ opacity: 0, y: placement === 'top' ? 10 : -10, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, scale: 0.96 }}
      transition={SPRING_CARD}
      className="fixed z-[8900] pointer-events-auto"
      style={{
        ...anchor,
        left,
        width,
        padding: 14,
        borderRadius: 20,
        background: 'rgba(16,20,36,0.92)',
        backdropFilter: 'blur(22px) saturate(170%)',
        WebkitBackdropFilter: 'blur(22px) saturate(170%)',
        border: '1px solid rgba(255,255,255,0.14)',
        boxShadow:
          '0 22px 52px -18px rgba(0,0,0,0.8), inset 0 1px 0 rgba(255,255,255,0.14)',
      }}
    >
      <div className="flex items-center justify-between gap-2 mb-2">
        <span
          className="font-mono"
          style={{
            fontSize: 9,
            letterSpacing: '0.22em',
            textTransform: 'uppercase',
            color: 'rgba(200,210,230,0.6)',
          }}
        >
          {t('onboarding.tour.progressLabel', { progress })}
        </span>
        <button
          type="button"
          onClick={onSkip}
          className="font-mono"
          style={{
            fontSize: 9,
            letterSpacing: '0.16em',
            textTransform: 'uppercase',
            color: 'rgba(200,210,230,0.5)',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            padding: '2px 4px',
            WebkitTapHighlightColor: 'transparent',
          }}
        >
          {t('onboarding.tour.skip')}
        </button>
      </div>

      <h3
        className="font-display"
        style={{
          fontSize: 16,
          fontWeight: 700,
          color: 'rgba(248,252,255,0.98)',
          letterSpacing: '-0.02em',
          lineHeight: 1.2,
          fontFamily:
            '-apple-system, BlinkMacSystemFont, "SF Pro Display", system-ui, sans-serif',
        }}
      >
        {title}
      </h3>
      <p
        className="mt-1.5"
        style={{
          fontSize: 12.5,
          lineHeight: 1.4,
          color: 'rgba(215,225,240,0.82)',
          wordBreak: 'break-word',
        }}
      >
        {body}
      </p>

      <motion.button
        type="button"
        onClick={onNext}
        whileTap={{ scale: 0.97 }}
        transition={SPRING_CARD}
        className="mt-3 w-full px-4 py-2.5 rounded-full font-semibold"
        style={{
          fontSize: 13,
          color: '#06091c',
          background: 'linear-gradient(180deg, #f5e6a8 0%, #e9c76a 100%)',
          border: '1px solid rgba(255,230,150,0.5)',
          boxShadow:
            '0 10px 24px -8px rgba(234,196,96,0.55), inset 0 1px 0 rgba(255,255,255,0.5)',
          cursor: 'pointer',
          WebkitTapHighlightColor: 'transparent',
        }}
      >
        {isLast ? t('onboarding.tour.done') : t('onboarding.tour.next')}
      </motion.button>
    </motion.div>
  );
}
